'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface TodayCheckInSummaryProps {
  contactId: string;
  refreshTrigger: number;
}

const TodayCheckInSummary = ({ contactId, refreshTrigger }: TodayCheckInSummaryProps) => {
  const [loading, setLoading] = useState(true);
  const [todayCheckIn, setTodayCheckIn] = useState<any>(null);
  const [voiceAnalysis, setVoiceAnalysis] = useState<any>(null);
  const [visualAssessment, setVisualAssessment] = useState<any>(null);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);

  useEffect(() => {
    fetchTodayCheckIn();
  }, [contactId, refreshTrigger]);

  const fetchTodayCheckIn = async () => {
    setLoading(true);
    const supabase = createClient();
    const today = new Date().toISOString().split('T')[0];

    try {
      // Fetch today's completed check-in
      const { data: checkIn, error: checkInError } = await supabase
        .from('check_in_calls')
        .select('*')
        .eq('contact_id', contactId)
        .eq('status', 'completed')
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (checkIn) {
        setTodayCheckIn(checkIn);

        // Fetch voice analysis
        const { data: voice } = await supabase
          .from('voice_analysis_scores')
          .select('*')
          .eq('call_id', checkIn.id)
          .single();
        setVoiceAnalysis(voice);

        // Fetch visual assessment
        const { data: visual } = await supabase
          .from('visual_assessment_results')
          .select('*')
          .eq('contact_id', contactId)
          .gte('created_at', `${today}T00:00:00`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        setVisualAssessment(visual);
      }
    } catch (error) {
      console.error('Error fetching today check-in:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-6 animate-pulse">
        <div className="h-6 bg-sage-200/30 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-48 bg-sage-200/30 rounded"></div>
          <div className="h-48 bg-sage-200/30 rounded"></div>
          <div className="h-48 bg-sage-200/30 rounded"></div>
        </div>
      </div>
    );
  }

  if (!todayCheckIn) {
    return (
      <div className="glass p-8 text-center">
        <Icon name="CalendarIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          No Check-In Today
        </h3>
        <p className="text-muted-foreground">
          No completed check-in call for today yet. Use the "Call Now" button below to initiate one.
        </p>
      </div>
    );
  }

  const responseData = todayCheckIn.response_data || {};
  const redFlags = todayCheckIn.red_flags || [];

  return (
    <div className="glass">
      <div className="p-6 border-b border-sage-200/30">
        <h2 className="text-2xl font-heading font-semibold text-foreground">
          Today's Check-In Summary
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {new Date(todayCheckIn.created_at).toLocaleString()}
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Voice Analysis */}
          <div className="bg-background/50 rounded-lg p-5 border border-sage-200/20">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="MicrophoneIcon" size={20} className="text-primary" />
              <h3 className="font-heading font-semibold text-foreground">VOICE ANALYSIS</h3>
            </div>
            {voiceAnalysis ? (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Cognitive Score</span>
                    <span className="text-2xl font-bold text-success">
                      {voiceAnalysis.cognitive_wellness_index}/100
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Vocabulary complexity: Normal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Speech coherence: Good</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Temporal orientation: Accurate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Emotional tone: Calm</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No voice analysis available</p>
            )}
          </div>

          {/* Visual Assessment */}
          <div className="bg-background/50 rounded-lg p-5 border border-sage-200/20">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="EyeIcon" size={20} className="text-secondary" />
              <h3 className="font-heading font-semibold text-foreground">VISUAL ASSESSMENT</h3>
            </div>
            {visualAssessment ? (
              <>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Visual Score</span>
                    <span className="text-2xl font-bold text-success">
                      {Math.round(
                        ((visualAssessment.eye_openness_score || 0) +
                          (visualAssessment.facial_symmetry_score || 0)) / 2
                      )}/100
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Posture: Normal alignment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Hand tremor: None detected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Facial symmetry: Normal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-foreground">Fall risk: Low</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Assessed via device camera
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No visual assessment today</p>
            )}
          </div>

          {/* Clinical Extraction */}
          <div className="bg-background/50 rounded-lg p-5 border border-sage-200/20">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ClipboardDocumentListIcon" size={20} className="text-accent" />
              <h3 className="font-heading font-semibold text-foreground">CLINICAL EXTRACTION</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success" />
                <span className="text-foreground">Medications taken</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success" />
                <span className="text-foreground">Ate breakfast and lunch</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground font-medium">
                  {responseData.pain_level || 3}/10 Pain level
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground">😊 Mood: {responseData.mood || 'Good'}</span>
              </div>
              {redFlags.length > 0 && (
                <div className="flex items-center gap-2">
                  <Icon name="ExclamationTriangleIcon" size={16} className="text-accent" />
                  <span className="text-accent">{redFlags[0]}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Extracted by Claude AI from call transcript
            </p>
          </div>
        </div>

        {/* Transcript Section */}
        <div className="border-t border-sage-200/30 pt-6">
          <button
            onClick={() => setTranscriptExpanded(!transcriptExpanded)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-smooth font-semibold"
          >
            <Icon
              name={transcriptExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={20}
            />
            {transcriptExpanded ? 'Hide' : 'View'} Full Transcript
          </button>

          {transcriptExpanded && (
            <div className="mt-4 bg-background/50 rounded-lg p-4 border border-sage-200/20">
              <div className="space-y-3 text-sm">
                {todayCheckIn.transcript ? (
                  todayCheckIn.transcript.split('\n').map((line: string, i: number) => {
                    const isHighlighted = redFlags.some((flag: string) =>
                      line.toLowerCase().includes(flag.toLowerCase())
                    );
                    return (
                      <p
                        key={i}
                        className={`${
                          isHighlighted
                            ? 'bg-[#E8A838]/20 border-l-4 border-[#E8A838] pl-3 py-1'
                            : ''
                        } text-foreground`}
                      >
                        {line}
                      </p>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground">No transcript available</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Transcribed by OpenAI Whisper
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodayCheckInSummary;